import routes from 'constants/routes'

/**
 * Where to go after signing in (MYD-541).
 *
 * A protected page reached while signed out (a notification email's link, a
 * bookmark, an expired session) sends the visitor to
 * `/login?redirect=<that page>`, and the login screen returns them there.
 *
 * The redirect arrives in the URL, so anyone can write one. It is honoured
 * only when it is a path inside this app: one leading slash, and nothing a
 * browser would read as another host. `//evil.example` and `/\evil.example`
 * are protocol-relative URLs to a browser, so both are refused, as is any
 * absolute URL. Anything refused falls back to the home page.
 */

/** A path inside the app, safe to navigate to. */
export const isSafeAppPath = (value: unknown): value is string =>
	typeof value === 'string' &&
	value.startsWith('/') &&
	!value.startsWith('//') &&
	!value.includes('\\') &&
	// No whitespace or control characters: browsers strip some of them, which
	// can turn a harmless-looking path into `//host`.
	!/\s/.test(value) &&
	![...value].some(char => char.charCodeAt(0) < 0x20 || char.charCodeAt(0) === 0x7f)

/** The redirect to follow after login, or `fallback` when there is none or it is unsafe. */
export const safeRedirect = (value: string | null | undefined, fallback: string = routes.HOME): string =>
	isSafeAppPath(value) ? value : fallback

/**
 * The login URL for a visitor turned away from `location`, remembering where
 * they were going. The home page and the login screen itself need no memory.
 */
export const loginPathFor = (location: { pathname: string; search?: string; hash?: string }): string => {
	const target = `${location.pathname}${location.search ?? ''}${location.hash ?? ''}`
	if (location.pathname === routes.HOME || location.pathname === routes.LOGIN || !isSafeAppPath(target)) return routes.LOGIN
	return `${routes.LOGIN}?redirect=${encodeURIComponent(target)}`
}
